import yup from '@/utils/yup-extended'
import { useTranslation } from 'react-i18next'

const VAT_REGEX = /^[A-Za-z0-9]+$/
const MIN_NAME_LENGTH = 4
const MIN_VAT_LENGTH = 1
const MAX_VAT_LENGTH = 20
const MIN_PASSWORD_LENGTH = 8
const NAME_REGEX =
  /^[^áàâäãåéèêëíìîïóòôöõúùûüýÿñçß.,:'"!@#№$+=%*()[\]{}<>:?/$]+[&_]?$/

interface UseAccountValidationSchemaProps {
  isRegistrationForm?: boolean
}

const useAccountValidationSchema = ({
  isRegistrationForm,
}: UseAccountValidationSchemaProps) => {
  const { t } = useTranslation(['translation', 'countries'])

  const AccountValidationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(t('Name is a required field'))
      .min(
        MIN_NAME_LENGTH,
        t('The value must be at least {{value}} characters long', {
          value: MIN_NAME_LENGTH,
        }),
      )
      .matches(
        NAME_REGEX,
        t(
          'The character you entered is not allowed. Please remove it and try again.',
        ),
      ),
    lastName: yup
      .string()
      .trim()
      .required(t('Last Name is a required field'))
      .matches(
        NAME_REGEX,
        t(
          'The character you entered is not allowed. Please remove it and try again.',
        ),
      ),
    companyName: yup
      .string()
      .trim()
      .required(t('Company Name is a required field'))
      .matches(
        NAME_REGEX,
        t(
          'The character you entered is not allowed. Please remove it and try again.',
        ),
      ),
    countryCodeId: yup
      .string()
      .typeError(t('Country is a required field'))
      .required(t('Country is a required field')),
    currencyId: yup
      .string()
      .typeError(t('Currency is a required field'))
      .required(t('Currency is a required field')),
    mobileTelephone: yup
      .string()
      .trim()
      .when({
        is: (val: string) => val.length,
        then: yup.string().trim().isValidPhone(t('isValidPhone')),
      })
      .nullable(true),
    companyAddress: yup
      .string()
      .trim()
      .required(t('Company Address is a required field')),
    vat: yup
      .string()
      .required(t('VAT number is a required field'))
      .matches(VAT_REGEX, t('Must be only digits and letters'))
      .min(
        MIN_VAT_LENGTH,
        t('The value must be at least {{value}} characters long', {
          value: MIN_VAT_LENGTH,
        }),
      )
      .max(
        MAX_VAT_LENGTH,
        t('The value must contain no more than {{value}} characters.', {
          value: MAX_VAT_LENGTH,
        }),
      )
      .typeError(t('VAT number is a required field')),
    sdi: yup.string().when('countryCodeId', {
      is: 'IT',
      then: yup.string().required(t('SDI/pec number is a required field')),
      otherwise: yup.string().nullable(),
    }),
    ...(isRegistrationForm && {
      email: yup
        .string()
        .trim()
        .email(t('Email is not correct'))
        .required(t('Email is a required field')),
      confirmEmail: yup
        .string()
        .trim()
        .oneOf([yup.ref('email'), null], t('Emails must match'))
        .required(t('Confirm Email is a required field')),
      password: yup
        .string()
        .trim()
        .required(t('Password is a required field'))
        .min(
          MIN_PASSWORD_LENGTH,
          t('Password must be at least characters', {
            number: MIN_PASSWORD_LENGTH,
          }),
        )
        .isValidPassword(t('isInvalidPassword')),
      confirmPassword: yup
        .string()
        .trim()
        .oneOf([yup.ref('password'), null], t('Passwords must match'))
        .required(t('Confirm Password is a required field')),
      privacyPolicyCheckBox: yup
        .boolean()
        .oneOf(
          [true],
          t('You must accept the privacy policy and terms of service'),
        )
        .required(t('You must accept the privacy policy and terms of service')),
    }),
  })

  return AccountValidationSchema
}

export default useAccountValidationSchema
